import { useEffect, useState } from 'react';
import type {
  MyEnrollment,
  PublicPlayer,
  PlayerProfile,
  PublicDirectorProfile,
  Tournament,
} from '@swiss-magic/shared-types';
import { getPublicPlayers, getPublicTournament } from '@/lib/api';
import { getProfile } from '@/lib/api/profile';
import { getMyEnrollment } from '@/lib/api/enrollment';
import { getMyFollow } from '@/lib/api/follow';
import { getMySubscription } from '@/lib/api/subscriptions';
import { getPublicDirectorProfile } from '@/lib/api/director-profile';
import { getOrgProfile, type OrganizationProfile } from '@/lib/api/org-profile';
import { currentTenantOrNull } from '@/lib/tenants';

interface PlayerData {
  visibility: 'list' | 'count' | 'hidden';
  players?: PublicPlayer[];
  count?: number;
}

export interface TournamentPageData {
  tournament: Tournament | null;
  playerData: PlayerData | null;
  directorProfile: PublicDirectorProfile | null;
  // Story 064 — populated on white-label tenants. The page renders the
  // org profile as the host and the director's name as the operator,
  // replacing the per-director attribution.
  orgProfile: OrganizationProfile | null;
  profile: PlayerProfile | null;
  myEnrollment: MyEnrollment | null;
  setMyEnrollment: (m: MyEnrollment | null) => void;
  following: boolean;
  setFollowing: (f: boolean) => void;
  subscribed: boolean;
  setSubscribed: (s: boolean) => void;
  loading: boolean;
  notFound: boolean;
}

/**
 * Loader hook for the public tournament page. Coordinates one parallel
 * fetch for the always-needed bits (tournament + player list + director
 * attribution) and a second parallel fetch for the viewer-specific
 * relationship state (profile + enrollment + follow + subscription)
 * when the viewer is logged in. Flips `loading` to false only after
 * both layers resolve, so the page never flashes a Register button at
 * a viewer who already enrolled.
 *
 * Setters for the viewer-specific state are exposed so the page's
 * action handlers (Enroll / Follow / Subscribe / Unregister / etc.)
 * can mutate after their respective API calls resolve.
 */
export function useTournamentPageData(
  slug: string | undefined,
  authLoading: boolean,
  user: { id: string } | null,
): TournamentPageData {
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [playerData, setPlayerData] = useState<PlayerData | null>(null);
  const [directorProfile, setDirectorProfile] =
    useState<PublicDirectorProfile | null>(null);
  const [orgProfile, setOrgProfile] = useState<OrganizationProfile | null>(null);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [myEnrollment, setMyEnrollment] = useState<MyEnrollment | null>(null);
  const [following, setFollowing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || authLoading) return;
    let cancelled = false;

    (async () => {
      try {
        const [t, p] = await Promise.all([
          getPublicTournament(slug),
          getPublicPlayers(slug),
        ]);
        if (cancelled) return;

        if (user) {
          const [freshProfile, enrollment, follow, subscription] = await Promise.all([
            getProfile().catch(() => null),
            getMyEnrollment(t.id).catch(() => null),
            getMyFollow(t.id).catch(() => ({ following: false })),
            getMySubscription(t.organizerId).catch(() => ({ subscribed: false })),
          ]);
          if (cancelled) return;
          setProfile(freshProfile);
          setMyEnrollment(enrollment);
          setFollowing(follow.following);
          setSubscribed(subscription.subscribed);
        }

        // Fetch the director profile in parallel (public endpoint). Failure is
        // non-fatal: the attribution block simply doesn't render.
        getPublicDirectorProfile(t.organizerId)
          .then((dp) => {
            if (!cancelled) setDirectorProfile(dp);
          })
          .catch(() => {
            /* no-op */
          });

        // Story 064 — only fetch the org profile on white-label tenants.
        // On the public offering it would auto-create an inert empty row
        // we don't want to litter the DB with.
        if (currentTenantOrNull()?.isWhiteLabel) {
          getOrgProfile()
            .then((op) => {
              if (!cancelled) setOrgProfile(op);
            })
            .catch(() => {
              /* no-op — fall through to director attribution */
            });
        }

        setTournament(t);
        setPlayerData(p);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, authLoading, user]);

  return {
    tournament,
    playerData,
    directorProfile,
    orgProfile,
    profile,
    myEnrollment,
    setMyEnrollment,
    following,
    setFollowing,
    subscribed,
    setSubscribed,
    loading,
    notFound,
  };
}
